import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Tabs, Tab } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import axiosClient from "../api/axiosClient";
import { API_BASE_URL } from "../config/api";
import FinancialFunnel from "../components/FinancialFunnel";
import UserJourney from "../components/UserJourney";
import RequestLogs from "../components/RequestLogs";
import UserManagement from "../components/UserManagement";
import BoostManagement from "../components/BoostManagement";
import MagnetManagement from "../components/MagnetManagement";
import RefillsManagement from "../components/RefillsManagement";
import DunningDashboard from "../components/DunningDashboard";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

// Admin dashboard component to display various CRO analytics
const Admin = () => {
  const [loading, setLoading] = useState(true);
  const [toolStats, setToolStats] = useState([]);
  const [boostStats, setBoostStats] = useState([]);
  const [toolSuccessRates, setToolSuccessRates] = useState([]);
  const [platformStats, setPlatformStats] = useState({
    platforms: [],
    subscriptions: [],
    platformSubscriptions: [],
  });
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    dailyGrowth: [],
  });
  const [activeTab, setActiveTab] = useState("financial");

  useEffect(() => {
    // Use Promise.all to fetch all endpoints concurrently.
    const fetchData = async () => {
      try {
        const [
          toolStatsResponse,
          boostStatsResponse,
          toolSuccessRatesResponse,
          platformStatsResponse,
          userStatsResponse,
        ] = await Promise.all([
          axiosClient.get("https://api.viralgpt.com/admin/tool-stats"),
          axiosClient.get("https://api.viralgpt.com/admin/boost-stats"),
          axiosClient.get("https://api.viralgpt.com/admin/tool-success-rates"),
          axiosClient.get("https://api.viralgpt.com/admin/platform-stats"),
          axiosClient.get("https://api.viralgpt.com/admin/user-stats"),
        ]);
        setToolStats(toolStatsResponse.data);
        setBoostStats(boostStatsResponse.data);
        setToolSuccessRates(toolSuccessRatesResponse.data);
        setPlatformStats(platformStatsResponse.data);
        setUserStats(userStatsResponse.data);
      } catch (error) {
        console.error("Error fetching admin logs data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Async ad spend sync — pull last 7 days from Meta API in background
    axiosClient.get("https://api.viralgpt.com/admin/ad-spend-sync")
      .then(res => console.log("[MetaAds] Ad spend synced:", res.data.synced?.length, "days"))
      .catch(err => console.warn("[MetaAds] Ad spend sync failed:", err.message));
  }, []);

  const userGrowthData = {
    labels: userStats.dailyGrowth.map((item) =>
      new Date(item.date).toLocaleDateString(),
    ),
    datasets: [
      {
        label: "New Users",
        data: userStats.dailyGrowth.map((item) => item.count),
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
    ],
  };

  const userGrowthOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Daily New Users (Last 30 Days)",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of New Users",
        },
      },
    },
    maintainAspectRatio: false,
  };

  const transformBoostStats = (boostStats) => {
    const platformActionStats = {};

    boostStats.forEach(({ eventName, count }) => {
      // Parse the event name: BOOST_ORDER_instagram_followers_5_START
      const parts = eventName.split("_");
      if (parts.length >= 4) {
        const platform = parts[2]; // instagram, tiktok, etc.
        const action = parts[3]; // followers, likes, etc.
        const status = parts[parts.length - 1]; // START, SUCCESS, FAILURE

        // Initialize nested objects if they don't exist
        platformActionStats[platform] = platformActionStats[platform] || {};
        platformActionStats[platform][action] = platformActionStats[platform][
          action
        ] || {
          starts: 0,
          successes: 0,
          failures: 0,
        };

        // Increment the appropriate counter
        if (status === "START")
          platformActionStats[platform][action].starts += count;
        else if (status === "SUCCESS")
          platformActionStats[platform][action].successes += count;
        else if (status === "FAILURE")
          platformActionStats[platform][action].failures += count;
      }
    });

    return platformActionStats;
  };

  // Prepare data for the "Tool Stats" chart (grouped bar chart)
  const toolStatsData = {
    labels: toolStats.map((item) => item.tool),
    datasets: [
      {
        label: "Starts",
        data: toolStats.map((item) => item.starts),
        backgroundColor: "rgba(153,102,255,0.6)",
      },
      {
        label: "Successes",
        data: toolStats.map((item) => item.successes),
        backgroundColor: "rgba(75,192,192,0.6)",
      },
      {
        label: "Failures",
        data: toolStats.map((item) => item.failures),
        backgroundColor: "rgba(255,99,132,0.6)",
      },
    ],
  };

  // Prepare data for the "Boost Stats" chart
  const boostStatsData = {
    labels: Object.keys(transformBoostStats(boostStats)).flatMap((platform) =>
      Object.keys(transformBoostStats(boostStats)[platform]).map(
        (action) => `${platform}\n${action}`,
      ),
    ),
    datasets: [
      {
        label: "Starts",
        data: Object.entries(transformBoostStats(boostStats)).flatMap(
          ([platform, actions]) =>
            Object.values(actions).map((stats) => stats.starts),
        ),
        backgroundColor: "rgba(153,102,255,0.6)",
      },
      {
        label: "Successes",
        data: Object.entries(transformBoostStats(boostStats)).flatMap(
          ([platform, actions]) =>
            Object.values(actions).map((stats) => stats.successes),
        ),
        backgroundColor: "rgba(75,192,192,0.6)",
      },
      {
        label: "Failures",
        data: Object.entries(transformBoostStats(boostStats)).flatMap(
          ([platform, actions]) =>
            Object.values(actions).map((stats) => stats.failures),
        ),
        backgroundColor: "rgba(255,99,132,0.6)",
      },
    ],
  };

  const boostChartOptions = {
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Count",
        },
      },
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
    plugins: {
      title: {
        display: true,
        text: "Boost Orders by Platform and Action Type",
      },
      legend: {
        position: "top",
      },
    },
    maintainAspectRatio: false,
  };

  // Prepare data for the "Tool Success Rates" chart
  const toolSuccessRatesData = {
    labels: toolSuccessRates.map((item) => item.tool),
    datasets: [
      {
        label: "Success Rate (%)",
        data: toolSuccessRates.map((item) => item.success_rate),
        backgroundColor: "rgba(75,192,192,0.6)",
      },
    ],
  };

  // Basic chart options to ensure y-axis starts from 0
  const chartOptions = {
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  if (loading) {
    return (
      <div className="stripe-section stripe-section--gradient" style={{minHeight: '100vh'}}>
        <div className="stripe-container px-4 text-center py-20">
          <div className="d-inline-flex align-items-center justify-content-center mb-3" style={{width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, #f0ebff 0%, #e8e0ff 100%)'}}>
            <i className="fas fa-spinner fa-spin" style={{color: '#635bff', fontSize: '1.25rem'}}></i>
          </div>
          <p className="stripe-body stripe-body--md stripe-text-muted">Loading admin data...</p>
        </div>
      </div>
    );
  }

  const platformDistributionData = {
    labels: platformStats?.platforms?.map((item) => item.platform) || [],
    datasets: [
      {
        label: "Number of Profiles",
        data: platformStats?.platforms?.map((item) => item.count) || [],
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
        ],
      },
    ],
  };

  const subscriptionDistributionData = {
    labels:
      platformStats?.subscriptions?.map(
        (item) => `${item.planName} (${item.planInterval})`,
      ) || [],
    datasets: [
      {
        label: "Active Subscriptions",
        data: platformStats?.subscriptions?.map((item) => item.count) || [],
        backgroundColor: [
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(255, 99, 132, 0.6)",
        ],
      },
    ],
  };

  const platformSubscriptionData = {
    labels: [
      ...new Set(
        platformStats?.platformSubscriptions?.map((item) => item.platform) ||
          [],
      ),
    ],
    datasets: [
      {
        label: "Basic Monthly",
        data: [
          ...new Set(
            platformStats?.platformSubscriptions?.map(
              (item) => item.platform,
            ) || [],
          ),
        ].map((platform) => {
          const match = platformStats?.platformSubscriptions?.find(
            (item) =>
              item.platform === platform &&
              item.planName === "basic" &&
              item.planInterval === "monthly",
          );
          return match ? match.count : 0;
        }),
        backgroundColor: "rgba(255, 99, 132, 0.6)",
      },
      {
        label: "Basic Annual",
        data: [
          ...new Set(
            platformStats?.platformSubscriptions?.map(
              (item) => item.platform,
            ) || [],
          ),
        ].map((platform) => {
          const match = platformStats?.platformSubscriptions?.find(
            (item) =>
              item.platform === platform &&
              item.planName === "basic" &&
              item.planInterval === "annual",
          );
          return match ? match.count : 0;
        }),
        backgroundColor: "rgba(54, 162, 235, 0.6)",
      },
      {
        label: "Pro Monthly",
        data: [
          ...new Set(
            platformStats?.platformSubscriptions?.map(
              (item) => item.platform,
            ) || [],
          ),
        ].map((platform) => {
          const match = platformStats?.platformSubscriptions?.find(
            (item) =>
              item.platform === platform &&
              item.planName === "pro" &&
              item.planInterval === "monthly",
          );
          return match ? match.count : 0;
        }),
        backgroundColor: "rgba(255, 206, 86, 0.6)",
      },
      {
        label: "Pro Annual",
        data: [
          ...new Set(
            platformStats?.platformSubscriptions?.map(
              (item) => item.platform,
            ) || [],
          ),
        ].map((platform) => {
          const match = platformStats?.platformSubscriptions?.find(
            (item) =>
              item.platform === platform &&
              item.planName === "pro" &&
              item.planInterval === "annual",
          );
          return match ? match.count : 0;
        }),
        backgroundColor: "rgba(75, 192, 192, 0.6)",
      },
    ],
  };

  const platformChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Platform Distribution",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Profiles",
        },
      },
    },
    maintainAspectRatio: false,
  };

  const subscriptionChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Subscription Distribution",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Subscriptions",
        },
      },
    },
    maintainAspectRatio: false,
  };

  const platformSubscriptionOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Subscription Distribution by Platform",
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Subscriptions",
        },
      },
    },
    maintainAspectRatio: false,
  };

  const stripeCardStyle = {
    background: '#fff', borderRadius: '16px', border: '1px solid #e3e8ee',
    boxShadow: '0 2px 4px rgba(50,50,93,0.06)', padding: '1.5rem', marginBottom: '1.5rem',
  };
  const stripeCardTitle = {
    fontSize: '0.8rem', fontWeight: 600, color: '#425466', textTransform: 'uppercase',
    letterSpacing: '0.08em', marginBottom: '1rem',
  };

  // Render the Analytics Dashboard content
  const renderAnalyticsDashboard = () => (
    <>
      <Row className="mb-4">
        <Col md={4}>
          <div style={stripeCardStyle}>
            <div style={stripeCardTitle}>Total Users</div>
            <h2 className="stripe-heading stripe-heading--xl stripe-text-dark text-center mb-0">
              {userStats.totalUsers.toLocaleString()}
            </h2>
          </div>
        </Col>
        <Col md={8}>
          <div style={stripeCardStyle}>
            <div style={stripeCardTitle}>User Growth</div>
            <div style={{ height: "400px" }}>
              <Bar data={userGrowthData} options={userGrowthOptions} />
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={4}>
          <div style={stripeCardStyle}>
            <div style={stripeCardTitle}>Platform Distribution</div>
            <div style={{ height: "400px" }}>
              <Bar data={platformDistributionData} options={platformChartOptions} />
            </div>
          </div>
        </Col>
        <Col md={4}>
          <div style={stripeCardStyle}>
            <div style={stripeCardTitle}>Subscription Distribution</div>
            <div style={{ height: "400px" }}>
              <Bar data={subscriptionDistributionData} options={subscriptionChartOptions} />
            </div>
          </div>
        </Col>
        <Col md={4}>
          <div style={stripeCardStyle}>
            <div style={stripeCardTitle}>Platform Subscription Distribution</div>
            <div style={{ height: "400px" }}>
              <Bar data={platformSubscriptionData} options={platformSubscriptionOptions} />
            </div>
          </div>
        </Col>
      </Row>

      {/* Tool Stats Chart */}
      <Row className="mb-4">
        <Col>
          <div style={stripeCardStyle}>
            <div style={stripeCardTitle}>Tool Usage</div>
            <Bar data={toolStatsData} options={chartOptions} />
          </div>
        </Col>
      </Row>

      {/* Tool Success Rates */}
      <Row>
        <Col md={6}>
          <div style={stripeCardStyle}>
            <div style={stripeCardTitle}>Tool Success</div>
            <Bar data={toolSuccessRatesData} options={chartOptions} />
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <div style={stripeCardStyle}>
            <div style={stripeCardTitle}>Boost Order Analysis</div>
            <div style={{ height: "400px" }}>
              <Bar data={boostStatsData} options={boostChartOptions} />
            </div>
          </div>
        </Col>
      </Row>
    </>
  );

  const renderMagnetManagement = () => (
    <>
    
           
              <MagnetManagement />
           
         
    </>
  );

  const renderRefills = () => (
    <>
      <RefillsManagement />
    </>
  );

  const adminTabs = [
    { key: 'financial', label: 'Revenue', icon: 'fa-dollar-sign' },
    { key: 'journey', label: 'User Journey', icon: 'fa-mouse-pointer' },
    { key: 'logs', label: 'Logs', icon: 'fa-list-alt' },
    { key: 'users', label: 'Users', icon: 'fa-users' },
    { key: 'boosts', label: 'Boosts', icon: 'fa-rocket' },
    { key: 'auto-followers', label: 'Auto Followers', icon: 'fa-sync-alt' },
    { key: 'refills', label: 'Refills', icon: 'fa-redo' },
    { key: 'dunning', label: 'Dunning', icon: 'fa-credit-card' },
    { key: 'analytics', label: 'Dashboard', icon: 'fa-chart-bar' },
  ];

  return (
    <div style={{background: 'linear-gradient(180deg, #f6f9fc 0%, #fff 100%)', minHeight: '100vh'}}>
      <div className="px-4 py-5">
        {/* Header */}
        <div className="d-flex align-items-center mb-5">
          <div className="me-auto">
            <h2 className="stripe-heading stripe-heading--lg stripe-text-dark mb-1">Admin Dashboard</h2>
            <p className="stripe-body stripe-body--sm stripe-text-muted mb-0">ViralGPT internal analytics &amp; management</p>
          </div>
          <div className="d-flex align-items-center" style={{gap: '0.5rem'}}>
            <img
              src="/images/luke_pfp.jpg"
              alt="Luke"
              className="rounded-circle stripe-avatar-ring"
              style={{ width: "48px", height: "48px" }}
            />
            <img
              src="/images/Mody_PFP.jpg"
              alt="Mody"
              className="rounded-circle stripe-avatar-ring"
              style={{ width: "48px", height: "48px" }}
            />
          </div>
        </div>

        {/* Stripe-styled Tab Navigation */}
        <div className="mb-5" style={{overflowX: 'auto', whiteSpace: 'nowrap', WebkitOverflowScrolling: 'touch', width: '100%', msOverflowStyle: 'none', scrollbarWidth: 'none'}}>
          <style>{`.admin-tabs-scroll::-webkit-scrollbar { display: none; }`}</style>
          <div className="admin-tabs-scroll d-flex" style={{background: '#fff', borderRadius: '14px', padding: '5px', border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)', gap: '4px', minWidth: 'max-content', width: '100%'}}>
            {adminTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="d-flex align-items-center justify-content-center flex-md-fill"
                style={{
                  padding: '0.6rem 0.75rem',
                  borderRadius: '10px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: activeTab === tab.key ? 600 : 500,
                  color: activeTab === tab.key ? '#fff' : '#425466',
                  background: activeTab === tab.key ? 'linear-gradient(135deg, #635bff 0%, #7c3aed 100%)' : 'transparent',
                  boxShadow: activeTab === tab.key ? '0 4px 6px rgba(50,50,93,0.11), 0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  gap: '0.35rem',
                  minWidth: 'fit-content',
                  flex: '1 1 auto',
                }}
              >
                <i className={`fas ${tab.icon}`} style={{fontSize: '0.75rem'}}></i>
                <span className="d-none d-lg-inline">{tab.label}</span>
                <span className="d-inline d-lg-none">{tab.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div style={{overflowX: 'hidden'}}>
          {activeTab === 'financial' && <FinancialFunnel />}
          {activeTab === 'journey' && <UserJourney />}
          {activeTab === 'logs' && <RequestLogs />}
          {activeTab === 'users' && <UserManagement />}
          {activeTab === 'boosts' && <BoostManagement />}
          {activeTab === 'auto-followers' && renderMagnetManagement()}
          {activeTab === 'refills' && renderRefills()}
          {activeTab === 'dunning' && <DunningDashboard />}
          {activeTab === 'analytics' && renderAnalyticsDashboard()}
        </div>
      </div>
    </div>
  );
};

export default Admin;
